import React from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Chip, Typography, TablePagination, Tooltip, Box, Card, CardContent, useMediaQuery, useTheme, Divider, Link, CircularProgress } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import EditIcon from "@mui/icons-material/Edit";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import FolderIcon from "@mui/icons-material/Folder";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import UserMenuChip from "./UserMenuChip";
import UserAssignmentDialog from "./UserAssignmentDialog";
import StatusBadge from "./StatusBadge";
import styled from "@emotion/styled";
import { useDispatch } from "react-redux";
import { toggleStarredItem } from "../redux/slices/users";
import CompleteCascadeDialog from "./CompleteCascadeDialog";
import { addUpTime, convertMinutesToHours } from "../utils/timeUtils";
import { formatDate } from "../utils/dateUtils";
import { daysUntilDue, dueTone } from "../utils/assignmentUtils";
import { tagDisplayName } from "./TagPicker";

const tagCodes = (item) => [...new Set((item.tags || []).map((entry) => tagDisplayName(entry.tag)))];

const TYPE_META = {
  Project: { label: "Project", color: "info", icon: <FolderIcon /> },
  Task: { label: "Task", color: "warning", icon: <TaskAltIcon /> },
  Quote: { label: "Quote", color: "secondary", icon: <RequestQuoteIcon /> },
};

const typeColor = (theme, type) => {
  if (type === "Task") return theme.palette.warning.main;
  if (type === "Quote") return theme.palette.secondary.main;
  return theme.palette.info.main;
};

const ResultsContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
  boxShadow: theme.shadows[2],
  height: '100%',
}));

const GroupByRow = styled(TableRow)(({ theme }) => ({
  display: "table-row",
  backgroundColor: theme.palette.action.hover,
  fontWeight: "bold",
}));

const ResultRow = styled(TableRow, { shouldForwardProp: (prop) => prop !== "resultType" })(({ theme, resultType }) => ({
  display: "table-row",
  borderBottom: `1px solid ${theme.palette.divider}`,
  borderLeft: `4px solid ${typeColor(theme, resultType)}`,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TableCellHorizontal = styled(TableCell)({
  display: "table-cell",
  borderBottom: "none",
});

const NameCell = styled(TableCellHorizontal, { shouldForwardProp: (prop) => prop !== "nested" })(({ theme, nested }) => ({
  paddingLeft: nested ? theme.spacing(5) : undefined,
}));

const OpenLink = styled(Link)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  fontWeight: theme.typography.fontWeightMedium,
  wordBreak: "break-word",
}));

const LinkIcon = styled(OpenInNewIcon)(({ theme }) => ({
  fontSize: `${theme.typography.size.sm} !important`,
  flexShrink: 0,
}));

const MetaRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexWrap: "wrap",
  gap: theme.spacing(0.75),
  marginTop: theme.spacing(0.5),
  "& .MuiChip-root": {
    height: "20px",
    fontSize: theme.typography.size.xs,
  },
}));

const ParentText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const SecondaryLink = styled(Link)(({ theme }) => ({
  color: theme.palette.text.secondary,
  display: "block",
}));

const TimeQuoted = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.primary,
  whiteSpace: "nowrap",
}));

const TimeActual = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  whiteSpace: "nowrap",
}));

const DueText = styled(Typography, { shouldForwardProp: (prop) => prop !== "dueState" })(({ theme, dueState }) => {
  const toneMap = {
    late: theme.palette.error.main,
    today: theme.palette.warning.dark,
    soon: theme.palette.mode === "dark" ? theme.palette.info.light : theme.palette.info.dark,
    normal: theme.palette.text.secondary,
  };
  return {
    color: toneMap[dueState] || toneMap.normal,
    fontWeight: dueState === "late" || dueState === "today" ? theme.typography.fontWeightMedium : theme.typography.fontWeightRegular,
    whiteSpace: "nowrap",
  };
});

const RightBox = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end"
}));

const GroupHeading = styled(Typography)(({ theme }) => ({
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(1),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
}));

const MobileCard = styled(Card, { shouldForwardProp: (prop) => prop !== "resultType" && prop !== "nested" })(({ theme, resultType, nested }) => ({
    marginBottom: theme.spacing(2),
    marginLeft: nested ? theme.spacing(3) : undefined,
    borderLeft: `4px solid ${typeColor(theme, resultType)}`,
}));

const CardHeaderRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(1),
}));

const ResponsibleRow = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(1),
}));

const GroupHeaderRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

const GroupLink = styled(Link)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  fontWeight: theme.typography.fontWeightBold,
  color: "inherit",
}));

const StateBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(6, 2),
    textAlign: 'center',
}));

const GROUP_TARGETS = {
  client: { href: (row) => (row.clientSlug ? `/client/${row.clientSlug}` : null), key: (row) => row.clientId },
  account: { href: (row) => (row.accountSlug ? `/account/${row.accountSlug}` : null), key: (row) => row.accountId },
  project: {
    href: (row) => (row.accountSlug && row.projectId ? `/account/${row.accountSlug}/project/${row.projectId}` : null),
    key: (row) => row.projectId,
    action: "project",
    canComplete: (items) => !items.some((row) => row.projectStatus === "Awaiting Authorization"),
  },
  group: {
    href: (row) => (row.accountSlug ? `/account/${row.accountSlug}` : null),
    key: (row) => row.groupId,
    action: "group",
  },
};

// A group key is only a name, so two different entities sharing one can merge into a single
// heading. Only offer a link or actions when every row in the group points at the same entity.
const resolveGroupTarget = (groupBy, items) => {
  const spec = GROUP_TARGETS[groupBy];
  if (!spec || items.length === 0) return null;
  const first = spec.key(items[0]);
  if (!first) return null;
  const consistent = items.every((row) => String(spec.key(row) || "") === String(first));
  if (!consistent) return null;
  const action = !spec.canComplete || spec.canComplete(items) ? spec.action : null;
  return { href: spec.href(items[0]), action, id: String(first) };
};

const itemHref = (item) => {
  if (item.type === "Quote") return `/quotes/${item._id}`;
  return item.accountSlug && item.projectId ? `/account/${item.accountSlug}/project/${item.projectId}` : null;
};

const itemLinkTitle = (item) => {
  if (item.type === "Quote") return "Open this quote in a new tab";
  return `Open this ${item.type === "Task" ? "task's project" : "project"} in a new tab`;
};

const STATUS_DOMAIN = { Task: "task", Quote: "quote" };
const accountHref = (item) => (item.accountSlug ? `/account/${item.accountSlug}` : null);
const clientHref = (item) => (item.clientSlug ? `/client/${item.clientSlug}` : null);

const NewTabLink = ({ href, title, children, component: Component = OpenLink, showIcon = true, variant = "body2" }) => {
  if (!href) return <Typography variant={variant}>{children}</Typography>;
  return (
    <Tooltip title={title}>
      <Component href={href} target="_blank" rel="noopener noreferrer" variant={variant} underline="hover">
        {children}
        {showIcon && <LinkIcon />}
      </Component>
    </Tooltip>
  );
};

const isEmptyValue = (value) => value === null || value === undefined || value === "";

const compareValues = (a, b) => {
  const aEmpty = isEmptyValue(a);
  const bEmpty = isEmptyValue(b);
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  if (a === b) return 0;
  return a > b ? 1 : -1;
};

const compareNames = (a, b) =>
  String(a?.name ?? "").localeCompare(String(b?.name ?? ""), undefined, { sensitivity: "base", numeric: true });

// A project leads the tasks that landed in its group, whatever order the sort left them in.
const nestTasksUnderProjects = (items) => {
  const projects = items.filter((item) => item.type === "Project");
  if (projects.length === 0) return items;

  const ownedTasks = new Map(projects.map((project) => [String(project.projectId), []]));
  const loose = [];
  items.forEach((item) => {
    if (item.type === "Project") return;
    const owner = ownedTasks.get(String(item.projectId));
    if (owner) owner.push(item);
    else loose.push(item);
  });

  return [
    ...projects.flatMap((project) => [project, ...ownedTasks.get(String(project.projectId))]),
    ...loose,
  ];
};

const projectRowIds = (items) =>
  new Set(items.filter((item) => item.type === "Project").map((item) => String(item.projectId)));

export const orderReportRows = (data, { sortBy = "", sortOrder = "desc", thenBy = "", groupBy = "" }) => {
  const direction = sortOrder === "desc" ? -1 : 1;

  const sorted = [...data].sort((a, b) => {
    if (sortBy) {
      const primary = compareValues(a[sortBy], b[sortBy]);
      if (primary !== 0) return primary * direction;
    }
    if (thenBy && thenBy !== sortBy) {
      const secondary = compareValues(a[thenBy], b[thenBy]);
      if (secondary !== 0) return secondary * direction;
    }
    return compareNames(a, b);
  });

  const groupedData = new Map();
  if (groupBy) {
    sorted.forEach((item) => {
      const groupKey = item[groupBy] || (item.type === "Quote" ? "Quotes" : "Ungrouped");
      if (!groupedData.has(groupKey)) {
        groupedData.set(groupKey, []);
      }
      groupedData.get(groupKey).push(item);
    });
  } else {
    groupedData.set("All Items", sorted);
  }

  return [...groupedData.entries()].flatMap(([group, items]) =>
    (groupBy ? nestTasksUnderProjects(items) : items).map(item => ({ ...item, groupKey: group }))
  );
};

const getRelativeDueDate = (dueDate) => {
  const diffDays = daysUntilDue(dueDate);
  if (diffDays === null) return "";

  if (diffDays < 0) {
    return `${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? "s" : ""} late`;
  } else if (diffDays === 0) {
    return "Due today";
  } else {
    return `Due in ${diffDays} day${diffDays !== 1 ? "s" : ""}`;
  }
};

const MasterReportResults = ({ data, isLoading, sortBy = "", sortOrder = "desc", thenBy = "", groupBy = "", onAddTimeClick, onTaskEditClick, onCompleteRefresh }) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [assignTarget, setAssignTarget] = React.useState(null);
  const [responsibleOverrides, setResponsibleOverrides] = React.useState({});
  const [starOverrides, setStarOverrides] = React.useState({});
  const [completeTarget, setCompleteTarget] = React.useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();

  const isStarred = (item) => (starOverrides[item._id] !== undefined ? starOverrides[item._id] : !!item.starred);

  const handleToggleStar = (item) => {
    const next = !isStarred(item);
    setStarOverrides((prev) => ({ ...prev, [item._id]: next }));
    dispatch(toggleStarredItem({ objectId: item._id, callback: (response) => {
      if (!response) {
        setStarOverrides((prev) => ({ ...prev, [item._id]: !next }));
        return;
      }
      setStarOverrides((prev) => ({ ...prev, [item._id]: !!response.starred }));
    } }));
  };

  const renderStarButton = (item) => {
    const starred = isStarred(item);
    return (
      <Tooltip title={starred ? "Remove from your starred items" : "Star this for yourself — only you see it"}>
        <IconButton size="small" onClick={() => handleToggleStar(item)}>
          {starred ? <StarIcon color="primary" /> : <StarBorderIcon color="disabled" />}
        </IconButton>
      </Tooltip>
    );
  };

  const handleOpenAssignDialog = (item) => setAssignTarget(item);
  const handleCloseAssignDialog = () => setAssignTarget(null);
  const handleAssigned = (objectId, _assignees, responsible) => {
    setResponsibleOverrides(prev => ({ ...prev, [objectId]: responsible || null }));
  };
  const getResponsible = (item) => {
    const current = responsibleOverrides[item._id] !== undefined ? responsibleOverrides[item._id] : item.responsible;
    return current && current._id ? current : null;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderGroupHeader = (group, items) => {
    const target = resolveGroupTarget(groupBy, items);
    const label = target?.href
      ? (
        <Tooltip title={`Open this ${groupBy} in a new tab`}>
          <GroupLink href={target.href} target="_blank" rel="noopener noreferrer" underline="hover">
            {group}
            <LinkIcon />
          </GroupLink>
        </Tooltip>
      )
      : <strong>{group}</strong>;

    const canComplete = Boolean(target?.action) && onCompleteRefresh;

    return (
      <GroupHeaderRow>
        {label}
        {canComplete && (
          <Tooltip title={`Complete this ${target.action} and everything inside it`}>
            <IconButton size="small" onClick={() => setCompleteTarget({ type: target.action, id: target.id, name: group })}>
              <DoneAllIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </GroupHeaderRow>
    );
  };

  const renderTypeChip = (item) => {
    const meta = TYPE_META[item.type] || { label: item.type, color: "default", icon: null };
    return <Chip icon={meta.icon} label={meta.label} color={meta.color} size="small" variant="filled" />;
  };

  const renderNameCell = (item, variant) => (
    <>
      <NewTabLink href={itemHref(item)} title={itemLinkTitle(item)} variant={variant}>
        {item.name}
      </NewTabLink>
      <MetaRow>
        {renderTypeChip(item)}
        {item.status && <StatusBadge domain={STATUS_DOMAIN[item.type] || "project"} status={item.status} />}
        {item.type === "Task" && item.project && groupBy !== "project" && <ParentText variant="caption">in {item.project}</ParentText>}
      </MetaRow>
    </>
  );

  const renderPersonChip = (item, size) => {
    if (item.type === "Quote") {
      const requester = item.requestedBy?._id ? item.requestedBy : null;
      return (
        <Tooltip title="Requested by">
          <span>
            <UserMenuChip user={requester} size={size} menuItems={{ viewProfile: !!requester, sendEmail: !!requester, scheduleMeeting: !!requester, reAssign: false }} />
          </span>
        </Tooltip>
      );
    }
    const responsible = getResponsible(item);
    return (
      <UserMenuChip user={responsible} size={size} onReAssign={() => handleOpenAssignDialog(item)} menuItems={{ viewProfile: !!responsible, sendEmail: !!responsible, scheduleMeeting: !!responsible, reAssign: true }} />
    );
  };

  const renderTagsChip = (item) => (
    <Tooltip title={tagCodes(item).join(", ")}>
      <Chip icon={<LocalOfferIcon />} label={`${tagCodes(item).length} tag${tagCodes(item).length !== 1 ? "s" : ""}`} size="small" variant="outlined" />
    </Tooltip>
  );

  const renderClientCell = (item) => (
    <>
      <NewTabLink href={clientHref(item)} title="Open this client in a new tab">
        {item.client}
      </NewTabLink>
      {item.account && item.account !== item.client && (
        <NewTabLink href={accountHref(item)} title="Open this account in a new tab" component={SecondaryLink} variant="caption">
          {item.account}
        </NewTabLink>
      )}
      {item.department && (
        <Typography variant="caption" color="textSecondary" display="block">
          {item.department}
        </Typography>
      )}
    </>
  );

  const flattenedData = orderReportRows(data, { sortBy, sortOrder, thenBy, groupBy });

  // Apply pagination to flattened data
  const paginatedData = flattenedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Regroup paginated data
  const paginatedGroupedData = paginatedData.reduce((acc, item) => {
    if (!acc.has(item.groupKey)) {
      acc.set(item.groupKey, []);
    }
    acc.get(item.groupKey).push(item);
    return acc;
  }, new Map());

  const renderTableView = () => (
    <ResultsContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox"></TableCell>
            <TableCell>Name & Type</TableCell>
            <TableCell>Client & Account</TableCell>
            <TableCell>Start & <br /> Due Dates</TableCell>
            <TableCell>Hours</TableCell>
            <TableCell>Responsible</TableCell>
            <TableCell>Tags</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody key="master-report-results-table-body">
          {[...paginatedGroupedData.entries()].map(([group, items]) => {
            const parentIds = projectRowIds(items);
            return (
            <React.Fragment key={`${group}`}>
              <GroupByRow>
                <TableCell colSpan={8}>{renderGroupHeader(group, items)}</TableCell>
              </GroupByRow>
              {items.map((item) => (
                <ResultRow key={`${item._id}`} resultType={item.type}>
                  <TableCellHorizontal padding="checkbox">{renderStarButton(item)}</TableCellHorizontal>
                  <NameCell nested={item.type !== "Project" && parentIds.has(String(item.projectId))}>{renderNameCell(item, "body2")}</NameCell>
                  <TableCellHorizontal>{renderClientCell(item)}</TableCellHorizontal>
                  <TableCellHorizontal>
                    <TimeQuoted variant="caption">{formatDate(item.startDate)}</TimeQuoted><br />
                    <DueText variant="caption" dueState={dueTone(item.dueDate)}>{getRelativeDueDate(item.dueDate)}</DueText>
                  </TableCellHorizontal>
                  <TableCellHorizontal>
                    {item.type === "Quote" ? (
                      <TimeQuoted variant="body2">Quoted: {item.totalHours} Hrs</TimeQuoted>
                    ) : (
                      <>
                        <TimeQuoted variant="body2">Est: {item.quotedTime} Hrs</TimeQuoted>
                        <TimeActual variant="caption">Actual: {addUpTime(convertMinutesToHours(item.actualTime))} Hrs</TimeActual>
                      </>
                    )}
                  </TableCellHorizontal>
                  <TableCellHorizontal>{renderPersonChip(item, "small")}</TableCellHorizontal>
                  <TableCellHorizontal>{item.type !== "Quote" && renderTagsChip(item)}</TableCellHorizontal>

                  <TableCellHorizontal>
                    { item.type === 'Task' && (
                      <Tooltip title="Edit this task">
                        <IconButton size="small" onClick={() => onTaskEditClick(item)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {item.type !== "Quote" && (
                      <Tooltip title="Log time against this item">
                        <IconButton size="small" onClick={() => onAddTimeClick(item)}>
                          <AccessTimeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCellHorizontal>
                </ResultRow>
              ))}
            </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </ResultsContainer>
  );

  const renderMobileView = () => (
    <Box>
      {[...paginatedGroupedData.entries()].map(([group, items]) => {
        const parentIds = projectRowIds(items);
        return (
        <React.Fragment key={group}>
          <GroupHeading variant="h6">
            {renderGroupHeader(group, items)}
          </GroupHeading>
          {items.map((item) => (
            <MobileCard key={item._id} resultType={item.type} nested={item.type !== "Project" && parentIds.has(String(item.projectId))}>
              <CardContent>
                <CardHeaderRow>
                  <Box>{renderNameCell(item, "subtitle1")}</Box>
                  {renderStarButton(item)}
                </CardHeaderRow>
                {renderClientCell(item)}
                <Typography variant="body2">Start: {formatDate(item.startDate)}</Typography>
                <DueText variant="body2" dueState={dueTone(item.dueDate)}>{getRelativeDueDate(item.dueDate)}</DueText>
                <Divider />
                {item.type === "Quote" ? (
                  <Typography variant="body2">Quoted: {item.totalHours} Hrs</Typography>
                ) : (
                  <Typography variant="body2">
                    Estimated: {addUpTime(convertMinutesToHours(item.quotedTime))} Hrs <br /> Actual: {addUpTime(convertMinutesToHours(item.actualTime))} Hrs
                  </Typography>
                )}
                <ResponsibleRow>
                  {renderPersonChip(item)}
                  {item.type !== "Quote" && (
                    <>
                      <br />
                      {renderTagsChip(item)}
                    </>
                  )}
                </ResponsibleRow>
                <RightBox>
                  { item.type === 'Task' && (
                    <Tooltip title="Edit this task">
                      <IconButton size="small" onClick={() => onTaskEditClick(item)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {item.type !== "Quote" && (
                    <Tooltip title="Log time against this item">
                      <IconButton size="small" onClick={() => onAddTimeClick(item)}>
                        <AccessTimeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </RightBox>
              </CardContent>
            </MobileCard>
          ))}
        </React.Fragment>
        );
      })}
    </Box>
  );

  const renderState = () => {
    if (isLoading) {
      return (
        <StateBox>
          <CircularProgress size={28} />
          <Typography variant="body1">Running your report…</Typography>
        </StateBox>
      );
    }
    return (
      <StateBox>
        <Typography variant="body1">No projects, tasks or quotes match these filters.</Typography>
        <Typography variant="body2" color="textSecondary">
          Try widening the date ranges, or set Status to "Active &amp; Completed" to include finished work.
        </Typography>
      </StateBox>
    );
  };

  return (
    <Paper>
      {isLoading || data.length === 0 ? (
        renderState()
      ) : (
        <>
          {isMobile ? renderMobileView() : renderTableView()}
          <TablePagination rowsPerPageOptions={[5, 10, 25, 50, 100]} component="div" count={flattenedData.length} rowsPerPage={rowsPerPage} page={page} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
        </>
      )}
      <CompleteCascadeDialog
        target={completeTarget}
        onClose={() => setCompleteTarget(null)}
        onCompleted={onCompleteRefresh}
      />

      <UserAssignmentDialog
        isOpen={!!assignTarget}
        onClose={handleCloseAssignDialog}
        objectId={assignTarget?._id}
        objectType={assignTarget?.type?.toLowerCase()}
        accountId={assignTarget?.accountId}
        singleSelection={true}
        onAssign={handleAssigned}
      />
    </Paper>
  );
};

export default MasterReportResults;
